import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { SessionSearchResultResponse } from "../../search/contracts/SessionSearchResultResponse";
import { AnnouncementAudience } from "../contracts/AnnouncementAudience";
import { AnnouncementAudiencePreviewResponse } from "../contracts/AnnouncementAudiencePreviewResponse";
import { AnnouncementDraftResponse } from "../contracts/AnnouncementDraftResponse";
import { AnnouncementResponse } from "../contracts/AnnouncementResponse";
import { DraftAnnouncementRequest } from "../contracts/DraftAnnouncementRequest";
import { ImproveAnnouncementRequest } from "../contracts/ImproveAnnouncementRequest";
import { CreateMessageTemplateRequest } from "../contracts/CreateMessageTemplateRequest";
import { MessageTemplateResponse } from "../contracts/MessageTemplateResponse";
import { PaginatedAnnouncementResult } from "../contracts/PaginatedAnnouncementResult";
import { SendAnnouncementRequest } from "../contracts/SendAnnouncementRequest";
import { UpdateMessageTemplateRequest } from "../contracts/UpdateMessageTemplateRequest";

export const searchAnnouncementSessions = createAsyncThunk<
  SessionSearchResultResponse[],
  string,
  { rejectValue: ApiErrorResponse }
>("announcements/searchSessions", async (query, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<SessionSearchResultResponse[]>("/bookings/search", {
      params: { query, limit: 8 },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const sendAnnouncement = createAsyncThunk<
  AnnouncementResponse,
  SendAnnouncementRequest,
  { rejectValue: ApiErrorResponse }
>("announcements/send", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<AnnouncementResponse>("/announcements", request);
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const draftAnnouncement = createAsyncThunk<
  AnnouncementDraftResponse,
  DraftAnnouncementRequest,
  { rejectValue: ApiErrorResponse }
>("announcements/draftWithAi", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<AnnouncementDraftResponse>(
      "/assistant/announcement/draft",
      request,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const improveAnnouncement = createAsyncThunk<
  AnnouncementDraftResponse,
  ImproveAnnouncementRequest,
  { rejectValue: ApiErrorResponse }
>("announcements/improveWithAi", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<AnnouncementDraftResponse>(
      "/assistant/announcement/improve",
      request,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const fetchAnnouncements = createAsyncThunk<
  PaginatedAnnouncementResult,
  { page?: number; pageSize?: number },
  { rejectValue: ApiErrorResponse }
>("announcements/fetch", async ({ page = 1, pageSize = 10 }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<PaginatedAnnouncementResult>("/announcements", {
      params: { page, pageSize },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const fetchMessageTemplates = createAsyncThunk<
  MessageTemplateResponse[],
  void,
  { rejectValue: ApiErrorResponse }
>("announcements/fetchTemplates", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<MessageTemplateResponse[]>("/message-templates");
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const createMessageTemplate = createAsyncThunk<
  MessageTemplateResponse,
  CreateMessageTemplateRequest,
  { rejectValue: ApiErrorResponse }
>("announcements/createTemplate", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<MessageTemplateResponse>("/message-templates", request);
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const updateMessageTemplate = createAsyncThunk<
  MessageTemplateResponse,
  { id: string; request: UpdateMessageTemplateRequest },
  { rejectValue: ApiErrorResponse }
>("announcements/updateTemplate", async ({ id, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<MessageTemplateResponse>(
      `/message-templates/${id}`,
      request,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const deleteMessageTemplate = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorResponse }
>("announcements/deleteTemplate", async (id, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`/message-templates/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/**
 * What the chosen audience would actually reach — so "send to all members" can show
 * the real number, and who is excluded, before the instructor commits.
 */
export const fetchAnnouncementAudience = createAsyncThunk<
  AnnouncementAudiencePreviewResponse,
  { audience: AnnouncementAudience; sessionId?: string },
  { rejectValue: ApiErrorResponse }
>("announcements/fetchAudience", async ({ audience, sessionId }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<AnnouncementAudiencePreviewResponse>(
      "/announcements/audience",
      { params: { audience, sessionId } },
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});
